import { PlayerController } from './PlayerController';
import { getVideo } from '@/api';
import { ClientAuth, Device, Logger } from '@/classes';
import { SECOND_MS } from '@/constants';
import { PopupContext } from '@/contexts/PopupContext';
import { usePlayer } from '@/hooks/usePlayer';
import { useRouter } from '@/hooks/useRouter';
import { Video } from '@/types';
import { getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import Hls from 'hls.js';
import isNil from 'lodash/isNil';
import { stringify } from 'qs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type PlayerProps = {
  duration?: number;
  language?: string;
  sourcePage?: string;
  containerId?: number;
} & (
  | {
      id: number;
      cursor: string;

      url?: never;
    }
  | {
      url: string;

      id?: never;
      cursor?: never;
    }
);

export const Player = ({ id, url, cursor, duration, language, sourcePage, containerId }: PlayerProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [video, setVideo] = useState<Video>();
  const [initialized, setInitialized] = useState(false);
  const popupContext = useContext(PopupContext);
  const player = usePlayer();

  const signedUrl = useMemo(() => {
    if (video) {
      return video.url;
    } else if (url) {
      return url;
    } else {
      return undefined;
    }
  }, [url, video]);

  const [videoError, setVideoError] = useState<unknown>(null);

  const load = useCallback(async (id: number, cursor: string, language?: string) => {
    setVideo(undefined);
    setLoading(true);
    setError(false);

    try {
      // ! 유료 콘텐츠 일 경우 팝업 & 로그인 체크 & 뒤로가기 등등..
      const hlsIsSupported = Hls.isSupported();
      const isOldBrowser = !Intl.Locale;
      const video = await getVideo({
        id,
        cursor,
        language: language || Device.videoLanguage,
        hls: hlsIsSupported && !isOldBrowser ? '1' : '0',
      });
      setVideoError(null);
      setVideo(video);
    } catch (e) {
      setVideoError(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const className = useMemo(() => {
    if (initialized && player.isLoaded) {
      return 'w-full h-full object-contain pointer-events-none';
    } else {
      return 'w-full h-full object-contain pointer-events-none invisible';
    }
  }, [initialized, player.isLoaded]);

  useEffect(() => {
    if (videoError) {
      if (!ClientAuth.user) {
        router.push('/login', { replace: true });
      } else if (videoError instanceof Response && videoError?.status === 402) {
        const currentFocusKey = getCurrentFocusKey();
        popupContext.showSubscribePopup('player', currentFocusKey, () => {
          router.push('/home', { replace: true });
        });
      } else if (videoError instanceof Response && videoError?.status === 404) {
        router.push('/home', { replace: true });
      } else {
        setError(true);
      }
    }
  }, [popupContext, router, videoError]);

  useEffect(() => {
    if (id) {
      Device.setHistory(id, { duration: player.currentTime, totalDuration: player.duration });
    }
  }, [id, player]);

  useEffect(() => {
    if (id) {
      load(id, cursor, language);
    }
  }, [cursor, id, language, load]);

  useEffect(() => {
    player.src(signedUrl);
  }, [player, signedUrl]);

  useEffect(() => {
    const current = player.ref.current;
    if (current) {
      if (video) {
        const nextContent = video.nextContent;
        if (nextContent && nextContent.type === 'VIDEO') {
          const onEnded = () => {
            if (!ClientAuth.user) {
              router.push('/login', { replace: true });
            } else if (!ClientAuth.user?.isSubscriber && nextContent.accessScope === 'SUBSCRIBER') {
              if (popupContext) {
                const currentFocusKey = getCurrentFocusKey();
                popupContext.showSubscribePopup('player', currentFocusKey);
                player.pause();
              }
            } else {
              router.push(
                `/videos?${stringify({
                  id: nextContent.id,
                  cursor: nextContent.cursor,
                  duration: 0,
                  source_page: sourcePage,
                  container_id: containerId,
                })}`,
                { replace: true },
              );
              player.setIsLoaded(false);
            }
          };

          current.addEventListener('ended', onEnded);

          return () => {
            current.removeEventListener('ended', onEnded);
          };
        } else {
          const onEnded = () => {
            if (!ClientAuth.user) {
              router.push('/login', { replace: true });
            } else {
              router.pop();
            }
          };

          current.addEventListener('ended', onEnded);

          return () => {
            current.removeEventListener('ended', onEnded);
          };
        }
      } else if (url) {
        const onEnded = () => {
          if (!ClientAuth.user) {
            router.push('/login', { replace: true });
          } else {
            router.pop();
          }
        };

        current.addEventListener('ended', onEnded);

        return () => {
          current.removeEventListener('ended', onEnded);
        };
      }
    }
  }, [containerId, player, popupContext, router, sourcePage, url, video]);

  useEffect(() => {
    if (player.isLoaded && !player.isPlaying && !initialized) {
      if (isNil(duration)) {
        if (id) {
          const history = Device.getHistory(id);
          if (history && history.duration >= 10 && history.duration <= history.totalDuration - 10) {
            popupContext.showLoadVideoPopup({
              onConfirm: () => {
                setInitialized(true);
                player.setIsLoaded(false);
                player.seek(history.duration);
                player.play();
              },
              onCancel: () => {
                setInitialized(true);
                player.play();
              },
            });
          } else {
            setInitialized(true);
            player.play();
          }
        } else {
          setInitialized(true);
          player.play();
        }
      } else if (duration) {
        setInitialized(true);
        player.setIsLoaded(false);
        player.seek(duration);
        player.play();
      } else {
        setInitialized(true);
        player.play();
      }
    }
  }, [id, duration, initialized, player, popupContext]);

  useEffect(() => {
    setInitialized(false);
  }, [signedUrl]);

  useEffect(() => {
    if (video) {
      player.setPreviewThumbnailCount(video.previewThumbnailCount || undefined);
    }
  }, [player, video]);

  useEffect(() => {
    if (Device.isTizen) {
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          popupContext.setPopupData({ open: false, onClose: () => {}, children: null });
          router.pop();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }
  }, [popupContext, router]);

  useEffect(() => {
    const parsedId = Number(id);

    if (parsedId) {
      const startedAt = Date.now();
      Logger.playStartRaw(parsedId, sourcePage, containerId);

      return () => {
        const duration = (Date.now() - startedAt) / SECOND_MS;
        Logger.playEndRaw(parsedId, duration, sourcePage, containerId);
      };
    }
  }, [containerId, id, sourcePage]);

  return (
    <div className="w-screen h-screen bg-black">
      {(!player.isLoaded || loading || player.isSeekLoading) && !error && !player.error && (
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-[rgba(0,0,0,0.2)]">
          <div className="w-[100px] h-[100px] border-4 border-yellow-300 border-t-transparent rounded-full animate-spin mb-4" />
        </div>
      )}

      {(error || player.error) && (
        <div className="absolute top-0 left-0 w-full h-full flex flex-row justify-center items-center bg-transparent">
          <svg width="100" height="100" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="28" stroke="#F87171" strokeWidth="4" fill="#FEE2E2" />
            <path d="M20 20 L40 40 M40 20 L20 40" stroke="#F87171" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </div>
      )}

      <video ref={player.ref} className={className} controls={false} />

      {player.isLoaded && initialized && (
        <PlayerController video={video} player={player} sourcePage={sourcePage} containerId={containerId} />
      )}
    </div>
  );
};
